'use client'

import { type FC } from 'react'

import { AdServerExperimentActivation } from '../ad-server'
import { AppsFlyerBanner } from '../apps-flyer-banner'
import { CartIntermediateContextProvider } from '../cart-intermediate'
import { CrossSellConfigProvider, CrossSellDialogDynamic } from '../cross-sell'
import { ErrorBoundary } from '../error-boundary'
import { useActivateAlgoliaDynamicRankingExperimentInPdp } from '../experiments/algolia-dynamic-ranking-experiment/useActivateAlgoliaDynamicRankingExperimentInPdp'
import { ExperimentBreadcrumbs } from '../experiments/components/ExperimentBreadcrumbs/ExperimentBreadcrumbs'
import { ExperimentFreeGiftPromotionActivation } from '../experiments/components/ExperimentFreeGiftPromotion/ExperimentFreeGiftPromotionActivation'
import { ExperimentOwnBrandsOverlay } from '../experiments/components/ExperimentOwnBrandsOverlay/ExperimentOwnBrandsOverlay'
import { isProductInOwnBrandsProductsMap } from '../experiments/components/ExperimentOwnBrandsOverlay/isProductInOwnBrandsProductsMap'
import { ExperimentsPageTypeProvider } from '../experiments/components/ExperimentsPageTypeContext'
import { ExperimentPageType } from '../experiments/ExperimentPageType'
import {
  useGetExperimentForTenant,
  useSetSearchSessionStorageValuesForOptimizelyTracking,
} from '../experiments/hooks'
import {
  EXPERIMENT_NAME_REDPOINTS_POPUP_IT,
  EXPERIMENT_OWN_BRANDS_OVERLAY,
} from '../experiments/model/EXPERIMENT_NAME_PER_TENANT'
import { RedpointsModal } from '../experiments/redpoints-modal-experiment/RedpointsModal'
import { FirstImpressionProvider } from '../first-impression'
import { useGlobalConfigContext } from '../global-config'
import { NowGeneralContextProvider } from '../now'
import { SellerModalProvider } from '../seller'
import { StandardShopLayout } from '../standard-shop-layout'

import { ProductPageAdServerPushCommand } from './components/ProductPageAdServerPushCommand'
import { ProductPageContextProvider } from './components/productPageContext'
import { ProductPageOverview } from './components/ProductPageCswPosition'
import { ProductPageJsonLdProductDescription } from './components/ProductPageJsonLdProductDescription/ProductPageJsonLdProductDescription'
import { ProductPageLatestActivity } from './components/ProductPageLatestActivity'
import { type ProductPageProps } from './components/ProductPageProps'
import { ProductOrderLimitContextProvider } from './product-order-limit/components/productOrderLimitContext'
import { ProductPageSeoHeadTags } from './seo/components/ProductPageSeoHeadTags/ProductPageSeoHeadTags'

export const ProductPage: FC<ProductPageProps> = ({
  crossSellConfig,
  experimentBreadCrumbs,
  firstImpression,
  productPageConfig,
  productPageData,
  standardLayoutData,
}) => {
  const ownBrandsLayoutExperiment = useGetExperimentForTenant(
    EXPERIMENT_OWN_BRANDS_OVERLAY,
  )
  const redpointsModalExperiment = useGetExperimentForTenant(
    EXPERIMENT_NAME_REDPOINTS_POPUP_IT,
  )

  useActivateAlgoliaDynamicRankingExperimentInPdp()

  const { tenant } = useGlobalConfigContext()

  useSetSearchSessionStorageValuesForOptimizelyTracking()

  /* eslint-disable react/jsx-max-depth -- the rule was disabled before a comment became required */
  return (
    <ExperimentsPageTypeProvider pageType={ExperimentPageType.Product}>
      <StandardShopLayout standardLayoutData={standardLayoutData}>
        <AppsFlyerBanner config={productPageData.appsFlyerBannerConfig} />
        <NowGeneralContextProvider>
          <FirstImpressionProvider firstImpression={firstImpression}>
            <ProductOrderLimitContextProvider
              config={productPageData.productOrderLimitConfig}
            >
              <ProductPageContextProvider
                channel={productPageData.channel}
                faqs={productPageData.faqs}
                followUpProduct={productPageData.followUpProduct}
                pageParams={productPageData.pageParams}
                product={productPageData.product}
                productPageConfig={productPageConfig}
                productPageOdsConfig={productPageData.productPageOdsConfig}
                promotionProduct={productPageData.promotionProduct}
                reviewData={productPageData.reviewData}
                shipping={productPageData.shipping}
                socialMediaConfig={productPageData.socialMediaConfig}
              >
                <ExperimentFreeGiftPromotionActivation />
                {ownBrandsLayoutExperiment.isEnabled &&
                isProductInOwnBrandsProductsMap(
                  productPageData.product.upid,
                  tenant,
                ) ? (
                  <ExperimentOwnBrandsOverlay
                    experimentVariation={ownBrandsLayoutExperiment.variant}
                    ownBrandsProduct={
                      productPageData.experimentOwnBrandsOverlayProduct
                    }
                  />
                ) : null}

                {redpointsModalExperiment.isEnabled ? (
                  <RedpointsModal
                    experimentName={redpointsModalExperiment.name}
                    experimentVariation={redpointsModalExperiment.variant}
                  />
                ) : null}
                <AdServerExperimentActivation />
                <ProductPageSeoHeadTags />
                <CrossSellConfigProvider value={crossSellConfig}>
                  <SellerModalProvider>
                    <CartIntermediateContextProvider>
                      <ProductPageAdServerPushCommand />
                      <ProductPageJsonLdProductDescription />
                      <ExperimentBreadcrumbs
                        experimentBreadCrumbs={experimentBreadCrumbs}
                      />
                      <div
                        className="container pt-5"
                        data-qa-id="product-details-page"
                      >
                        <ProductPageOverview />
                        <ErrorBoundary>
                          <ProductPageLatestActivity />
                        </ErrorBoundary>
                      </div>
                      <CrossSellDialogDynamic />
                    </CartIntermediateContextProvider>
                  </SellerModalProvider>
                </CrossSellConfigProvider>
              </ProductPageContextProvider>
            </ProductOrderLimitContextProvider>
          </FirstImpressionProvider>
        </NowGeneralContextProvider>
      </StandardShopLayout>
    </ExperimentsPageTypeProvider>
  )
  /* eslint-enable react/jsx-max-depth */
}
